import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuthStore } from '../auth/store';
import { dayLabel, fmtDuration } from '../date';

// ── Brand tokens (from design.json) ──────────────────────────────────────────
const BRAND = '#313380';
const BRAND_DARK = '#1d1e4d';
const SECONDARY = '#008ce1';
const BORDER = '#eaeaea';
const TEXT = '#222222';
const TEXT_MUTED = '#666666';
const TEXT_SUBTLE = '#888888';
const SUCCESS = '#16a34a';
const WARNING = '#d97706';
const WHITE = '#ffffff';
const SURFACE_MUTED = '#f4f4f4';

// ── Types mirroring the API response ─────────────────────────────────────────
interface Slot {
  channelName: string;
  locale: string;
  dayOfWeek: number;
  startTime: string;
  durationMinutes: number;
  isActive: boolean;
}

interface Episode {
  title: string;
  locale: string;
  airDateGmt: string;
  durationSeconds: number | null;
  publishedAt: string | null;
}

interface Programme {
  slug: string;
  title: string;
  summary: string | null;
  defaultLocale: string;
  isActive: boolean;
  medium: string;
  slots: Slot[];
  episodes?: Episode[];
}

interface ExportPayload {
  exportedAt: string;
  items: Programme[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ── Fetch data ────────────────────────────────────────────────────────────────
async function fetchProgrammesData(locale: string): Promise<ExportPayload> {
  const token = useAuthStore.getState().token;
  const baseUrl = (() => {
    const env = import.meta.env.VITE_API_URL;
    if (env) return env.replace(/\/$/, '');
    if (import.meta.env.DEV) return '/api';
    return '';
  })();

  const url = `${baseUrl}/admin/export/programmes?format=json&locale=${encodeURIComponent(locale)}&includeEpisodes=1`;
  const res = await fetch(url, {
    headers: { authorization: token ? `Bearer ${token}` : '' },
  });
  if (!res.ok) throw new Error(`Failed to fetch programmes: ${res.status}`);
  return res.json() as Promise<ExportPayload>;
}

// ── Logo ──────────────────────────────────────────────────────────────────────
async function loadLogoDataUrl(): Promise<string | null> {
  try {
    const res = await fetch('/logo.png');
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ── Page header / footer ──────────────────────────────────────────────────────
function drawPageHeader(doc: jsPDF, logoDataUrl: string | null, pageWidth: number): number {
  // Header bar
  doc.setFillColor(...rgb(BRAND));
  doc.rect(0, 0, pageWidth, 22, 'F');

  // Logo
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', 6, 3, 14, 15);
  }

  // Organisation name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...rgb(WHITE));
  doc.text('Voice of Nigeria Broadcasting Service', logoDataUrl ? 24 : 8, 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...rgb('#c5c7e8'));
  doc.text('Programmes & Schedule Report', logoDataUrl ? 24 : 8, 16);

  return 28; // y after header
}

function drawPageFooter(doc: jsPDF, pageNum: number, totalPages: number, exportedAt: string): void {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const y = pageHeight - 8;

  doc.setDrawColor(...rgb(BORDER));
  doc.setLineWidth(0.3);
  doc.line(10, y - 3, pageWidth - 10, y - 3);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...rgb(TEXT_SUBTLE));
  doc.text(`Generated ${fmtDate(exportedAt)} · Voice of Nigeria Admin`, 10, y);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 10, y, { align: 'right' });
}

// ── Cover page ────────────────────────────────────────────────────────────────
function drawCoverPage(
  doc: jsPDF,
  logoDataUrl: string | null,
  locale: string,
  exportedAt: string,
  programmeCount: number,
): void {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();

  // Full-bleed top band
  doc.setFillColor(...rgb(BRAND));
  doc.rect(0, 0, pw, ph * 0.45, 'F');

  // Accent stripe
  doc.setFillColor(...rgb(SECONDARY));
  doc.rect(0, ph * 0.45, pw, 3, 'F');

  // Logo centred in band
  if (logoDataUrl) {
    const lw = 36;
    const lh = 38;
    doc.addImage(logoDataUrl, 'PNG', (pw - lw) / 2, ph * 0.1, lw, lh);
  }

  // Org name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...rgb(WHITE));
  doc.text('Voice of Nigeria', pw / 2, ph * 0.45 - 28, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...rgb('#c5c7e8'));
  doc.text('Broadcasting Service', pw / 2, ph * 0.45 - 20, { align: 'center' });

  // Report title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...rgb(BRAND_DARK));
  doc.text('Programmes & Schedule', pw / 2, ph * 0.55, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...rgb(TEXT_MUTED));
  doc.text('Full schedule slots and episode listing', pw / 2, ph * 0.55 + 9, { align: 'center' });

  // Meta pills
  const metaY = ph * 0.68;
  const pillW = 52;
  const pillH = 14;
  const gap = 6;
  const totalW = pillW * 3 + gap * 2;
  let px = (pw - totalW) / 2;

  const pills = [
    { label: 'Locale', value: locale.toUpperCase() },
    { label: 'Programmes', value: String(programmeCount) },
    { label: 'Generated', value: fmtDate(exportedAt) },
  ];

  for (const pill of pills) {
    doc.setFillColor(...rgb(SURFACE_MUTED));
    doc.setDrawColor(...rgb(BORDER));
    doc.setLineWidth(0.4);
    doc.roundedRect(px, metaY, pillW, pillH, 2, 2, 'FD');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...rgb(TEXT_SUBTLE));
    doc.text(pill.label, px + pillW / 2, metaY + 4.5, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...rgb(BRAND));
    doc.text(pill.value, px + pillW / 2, metaY + 10.5, { align: 'center' });

    px += pillW + gap;
  }

  // Footer tagline
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...rgb(TEXT_SUBTLE));
  doc.text(
    'Uplifting the People and Uniting the Nation',
    pw / 2,
    ph - 14,
    { align: 'center' },
  );
}

// ── Programme section ─────────────────────────────────────────────────────────
function drawProgrammeSection(
  doc: jsPDF,
  prog: Programme,
  startY: number,
): number {
  const pw = doc.internal.pageSize.getWidth();
  const margin = 10;
  const contentW = pw - margin * 2;
  let y = startY;

  // Programme title bar
  doc.setFillColor(...rgb(BRAND));
  doc.roundedRect(margin, y, contentW, 10, 1.5, 1.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...rgb(WHITE));
  doc.text(prog.title, margin + 4, y + 6.8);

  // Status + medium badges (right-aligned)
  const badgeTexts = [
    prog.isActive ? 'Active' : 'Inactive',
    prog.medium === 'radio' ? 'Radio' : prog.medium === 'tv' ? 'TV' : 'Simulcast',
  ];
  let bx = margin + contentW - 4;
  for (const bt of [...badgeTexts].reverse()) {
    const bw = doc.getTextWidth(bt) + 6;
    bx -= bw + 2;
    doc.setFillColor(...rgb(prog.isActive ? SUCCESS : WARNING));
    doc.roundedRect(bx, y + 2, bw, 6, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...rgb(WHITE));
    doc.text(bt, bx + bw / 2, y + 6.2, { align: 'center' });
  }

  y += 13;

  // Summary
  if (prog.summary) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...rgb(TEXT_MUTED));
    const lines = doc.splitTextToSize(prog.summary, contentW - 4) as string[];
    doc.text(lines, margin + 2, y);
    y += lines.length * 4 + 2;
  }

  // ── Slots table ──
  if (prog.slots.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...rgb(BRAND_DARK));
    doc.text('Schedule Slots', margin + 2, y + 4);
    y += 7;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Channel', 'Locale', 'Day', 'Start', 'Duration', 'Status']],
      body: prog.slots.map((s) => [
        s.channelName,
        s.locale.toUpperCase(),
        dayLabel(s.dayOfWeek),
        s.startTime,
        fmtDuration(s.durationMinutes * 60),
        s.isActive ? 'Active' : 'Inactive',
      ]),
      headStyles: {
        fillColor: rgb(BRAND_DARK),
        textColor: rgb(WHITE),
        fontStyle: 'bold',
        fontSize: 7.5,
        cellPadding: 2.5,
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: rgb(TEXT),
        cellPadding: 2.2,
      },
      alternateRowStyles: { fillColor: rgb(SURFACE_MUTED) },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 16, halign: 'center' },
        2: { cellWidth: 22 },
        3: { cellWidth: 18, halign: 'center', font: 'courier' },
        4: { cellWidth: 22, halign: 'right' },
        5: {
          cellWidth: 18,
          halign: 'center',
          textColor: rgb(SUCCESS),
          fontStyle: 'bold',
        },
      },
      didParseCell(data) {
        if (data.column.index === 5 && data.section === 'body') {
          const val = data.cell.raw as string;
          data.cell.styles.textColor = rgb(val === 'Active' ? SUCCESS : WARNING);
        }
      },
      tableLineColor: rgb(BORDER),
      tableLineWidth: 0.2,
    });

    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(...rgb(TEXT_SUBTLE));
    doc.text('No schedule slots configured.', margin + 2, y + 4);
    y += 9;
  }

  // ── Episodes table ──
  if (prog.episodes && prog.episodes.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...rgb(BRAND_DARK));
    doc.text(`Episodes (${prog.episodes.length})`, margin + 2, y + 4);
    y += 7;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Title', 'Locale', 'Air Date', 'Duration', 'Status']],
      body: prog.episodes.map((e) => [
        e.title,
        e.locale.toUpperCase(),
        fmtDate(e.airDateGmt),
        e.durationSeconds ? fmtDuration(e.durationSeconds) : '—',
        e.publishedAt ? 'Published' : 'Draft',
      ]),
      headStyles: {
        fillColor: rgb(SECONDARY),
        textColor: rgb(WHITE),
        fontStyle: 'bold',
        fontSize: 7.5,
        cellPadding: 2.5,
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: rgb(TEXT),
        cellPadding: 2.2,
      },
      alternateRowStyles: { fillColor: rgb(SURFACE_MUTED) },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 14, halign: 'center' },
        2: { cellWidth: 26 },
        3: { cellWidth: 22, halign: 'right' },
        4: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
      },
      didParseCell(data) {
        if (data.column.index === 4 && data.section === 'body') {
          const val = data.cell.raw as string;
          data.cell.styles.textColor = rgb(val === 'Published' ? SUCCESS : WARNING);
        }
      },
      tableLineColor: rgb(BORDER),
      tableLineWidth: 0.2,
    });

    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
  }

  return y + 6; // spacing before next programme
}

// ── Main export function ──────────────────────────────────────────────────────
export async function exportProgrammesPdf(locale: string): Promise<void> {
  const data = await fetchProgrammesData(locale);
  const logoDataUrl = await loadLogoDataUrl();

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const footerH = 12;

  // Cover page (no header/footer)
  drawCoverPage(doc, logoDataUrl, locale, data.exportedAt, data.items.length);

  // Content pages
  let pageNum = 1;

  for (const prog of data.items) {
    doc.addPage();
    pageNum++;
    let y = drawPageHeader(doc, logoDataUrl, pw);

    // Check if we need a new page mid-programme (autoTable handles its own
    // page breaks, but we need to re-draw the header on each new page)
    const estimatedH = 14 + (prog.summary ? 12 : 0) + prog.slots.length * 6 + 20;
    if (y + estimatedH > ph - footerH) {
      doc.addPage();
      pageNum++;
      y = drawPageHeader(doc, logoDataUrl, pw);
    }

    drawProgrammeSection(doc, prog, y);
  }

  // Draw footers on all content pages (cover is page 1, content starts at 2)
  const totalPages = doc.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    drawPageFooter(doc, i - 1, totalPages - 1, data.exportedAt);
  }

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`von-programmes-${locale}-${stamp}.pdf`);
}
